// DynamicTable.js - Generic table that adapts to schema definitions with line item support
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Save, ThumbsUp, AlertTriangle, Split, Trash2, Eye } from 'lucide-react';
import SchemaFieldRenderer from 'components/schema/SchemaFieldRenderer';
import schemaService from 'services/SchemaService';
import styles from './DynamicTable.module.css';

function DynamicTable({ 
  records = [], 
  onRecordUpdate, 
  onRecordSelect, 
  selectedRecords = new Set(),
  maxColumns = 8,
  onOpenMessagePanel,
  onToggleResolved,
  onViewDetails
}) {
  const [schemas, setSchemas] = useState({});
  const [lookupData, setLookupData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editedRecords, setEditedRecords] = useState(new Map());
  const [localRecords, setLocalRecords] = useState(records); // Local copy for immediate visual updates
  const [loading, setLoading] = useState(true);
  const [savingRecords, setSavingRecords] = useState(new Set()); // Track which records are being saved
  const [openLineItemsPanel, setOpenLineItemsPanel] = useState(null);
  const [hasSeenMixedTypes, setHasSeenMixedTypes] = useState(false); // Track if we've seen mixed types
  const [cachedMixedColumns, setCachedMixedColumns] = useState(null); // Cache columns for mixed types
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null }); // Track sorting state

  // Sync local records when props change
  useEffect(() => {
    setLocalRecords(records);
  }, [records]);

  // Get unique record types from records
  const recordTypes = useMemo(() => {
    return [...new Set(localRecords.map(r => {
      // Handle both string and array record_type
      const recordType = r.record_type;
      return Array.isArray(recordType) ? recordType[0] : recordType;
    }))];
  }, [localRecords]);

  // Load schemas and lookup data for all record types with fallback
  useEffect(() => {
    const loadSchemasAndLookups = async () => {
      try {
        setLoading(true);
        
        // Try to load schemas for all record types
        const schemaMap = {};
        const allLookupData = {};
        
        for (const recordType of recordTypes) {
          try {
            const schema = schemaService.getSchemaForRecord(recordType);
            schemaMap[recordType] = schema;
            
            // Load lookup data for this record type
            const lookups = await schemaService.getRequiredLookupData(recordType);
            Object.assign(allLookupData, lookups);
          } catch (error) {
            console.error(`Schema not available for ${recordType}:`, error);
            // Do not create fallback - leave empty to show error
            schemaMap[recordType] = null;
          }
        }
        
        setSchemas(schemaMap);
        setLookupData(allLookupData);
      } catch (error) {
        console.error('Schema service not available:', error);
        // Do not create any fallback schemas - show error state
        setSchemas({});
      } finally {
        setLoading(false);
      }
    };

    if (recordTypes.length > 0) {
      loadSchemasAndLookups();
    } else {
      setLoading(false);
    }
  }, [recordTypes]);


  // Get common high-value fields across record types
  const getCommonHighValueFields = useCallback((types, maxFields) => {
    const fieldFrequency = new Map();
    const fieldDisplayOrders = new Map();
    
    types.forEach(recordType => {
      try {
        let displayFields;
        try {
          displayFields = schemaService.getDisplayFields(recordType);
        } catch (error) {
          // Fallback to using our local schema
          const schema = schemas[recordType] || {};
          displayFields = schema;
        }
        
        Object.entries(displayFields).forEach(([fieldName, fieldDef]) => {
          const count = fieldFrequency.get(fieldName) || 0;
          const displayOrder = fieldDisplayOrders.get(fieldName) || 999;
          
          fieldFrequency.set(fieldName, count + 1);
          fieldDisplayOrders.set(fieldName, Math.min(displayOrder, fieldDef.display_order || 999));
        });
      } catch (error) {
        console.warn(`Error analyzing fields for ${recordType}:`, error);
      }
    });
    
    const sortedFields = Array.from(fieldFrequency.entries())
      .sort(([a, aCount], [b, bCount]) => {
        if (aCount !== bCount) return bCount - aCount;
        return (fieldDisplayOrders.get(a) || 999) - (fieldDisplayOrders.get(b) || 999);
      })
      .slice(0, maxFields)
      .map(([fieldName]) => fieldName);
      
    return sortedFields;
  }, [schemas]);

  // Generate dynamic columns based on schemas
  const columns = useMemo(() => {
    if (recordTypes.length === 0) return [];
    
    // For single record type, always use its specific fields
    if (recordTypes.length === 1) {
      const recordType = recordTypes[0];
      const schema = schemas[recordType];
      
      // If no schema available, show error state
      if (!schema) {
        return [
          { key: 'select', label: '', width: '3rem', fixed: true },
          { key: 'error', label: `No schema available for ${recordType}`, width: '1fr', fixed: true },
          { key: 'actions', label: 'Actions', width: '12rem', fixed: true }
        ];
      }
      
      let displayFields;
      
      try {
        displayFields = schemaService.getDisplayFields(recordType, maxColumns - 3); // -3 for select, record_type, actions
      } catch (error) {
        console.warn('Schema service failed, using local schema:', error);
        // Use local schema only if it exists
        
        // Filter and sort fields by display_order, excluding meta fields
        const sortedFields = Object.entries(schema)
          .filter(([fieldName, fieldDef]) => {
            return !fieldName.startsWith('_') && 
                   fieldName !== 'extends' && 
                   fieldName !== 'type' &&
                   fieldDef.display_order !== undefined;
          })
          .sort(([, a], [, b]) => (a.display_order || 999) - (b.display_order || 999))
          .slice(0, maxColumns - 3); // -3 for select, record_type, actions
        
        displayFields = Object.fromEntries(sortedFields);
      }
      
      return [
        { key: 'select', label: '', width: '3rem', fixed: true },
        { key: 'rule_name', label: 'Monitor', width: '8rem', fixed: true, sortable: true },
        { key: 'record_type', label: 'Type', width: '6rem', fixed: true, sortable: true },
        ...Object.entries(displayFields).filter(([fieldName]) => fieldName !== 'rule_name').map(([fieldName, fieldDef]) => ({
          key: fieldName,
          label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          fieldDef,
          recordType,
          sortable: true,
          width: fieldDef.type === 'string' ? '2fr' : '1fr'
        })),
        { key: 'actions', label: 'Actions', width: '12rem', fixed: true }
      ];
    }
    
    // For multiple record types, check if we should use cached columns
    if (recordTypes.length > 1 && hasSeenMixedTypes && cachedMixedColumns) {
      return cachedMixedColumns;
    }
    
    // For multiple record types, find common high-value fields
    const commonFields = getCommonHighValueFields(recordTypes, maxColumns - 3); // Reserve space for lines
    
    // Ensure critical fields are included even if not "common"
    const criticalFields = ['rule_name', 'notes', 'organization', 'total_amount', 'txn_date', 'date'];
    const missingCritical = criticalFields.filter(field => !commonFields.includes(field));
    
    if (missingCritical.length > 0) {
      commonFields.push(...missingCritical.slice(0, Math.max(0, maxColumns - 3 - commonFields.length)));
    }
    
    return [
      { key: 'select', label: '', width: '3rem', fixed: true },
      { key: 'rule_name', label: 'Monitor', width: '8rem', fixed: true, sortable: true },
      { key: 'record_type', label: 'Type', width: '6rem', fixed: true, sortable: true },
      ...commonFields.filter(field => field !== 'line_items' && field !== 'rule_name').map(fieldName => ({
        key: fieldName,
        label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        fieldDef: { 
          type: fieldName.includes('amount') || fieldName.includes('balance') ? 'number' : 'string', 
          editable: fieldName === 'notes' || fieldName === 'status' 
        },
        sortable: true,
        width: '1.5fr'
      })),
      { key: 'line_items', label: 'Lines', width: '2fr', fixed: true },
      { key: 'actions', label: 'Actions', width: '12rem', fixed: true }
    ];
  }, [recordTypes, maxColumns, schemas, hasSeenMixedTypes, cachedMixedColumns, getCommonHighValueFields]);

  // Track when we have mixed types and cache the columns
  useEffect(() => {
    if (recordTypes.length > 1) {
      if (!hasSeenMixedTypes) {
        setHasSeenMixedTypes(true);
      }
      // Cache columns for mixed types if we don't have them yet
      if (!cachedMixedColumns && columns.length > 0) {
        setCachedMixedColumns(columns);
      }
    } else if (recordTypes.length <= 1) {
      // Reset when we go back to single type or no types
      setHasSeenMixedTypes(false);
      setCachedMixedColumns(null);
    }
  }, [recordTypes.length, columns, hasSeenMixedTypes, cachedMixedColumns]);

  // Get field value for display (now uses localRecords which has immediate updates)
  const getFieldValue = (record, fieldName) => {
    let value;
    
    // For normalized data, check rec_normalized first
    if (record.rec_normalized && record.rec_normalized[fieldName] !== undefined) {
      value = record.rec_normalized[fieldName];
    }
    // Check top-level record (some fields like notes might be here)
    else if (record[fieldName] !== undefined) {
      value = record[fieldName];
    }
    // Handle field mapping aliases
    else if (fieldName === 'txn_date' && record.date) {
      value = record.date;
    }
    else if (fieldName === 'date' && record.rec_normalized?.txn_date) {
      value = record.rec_normalized.txn_date;
    }
    
    // Special handling for organization field - it might be an object
    if (fieldName === 'organization' && value && typeof value === 'object') {
      // Try common object property names for organization
      return value.name || value.Name || value.DisplayName || value.title || value.value || JSON.stringify(value);
    }
    
    return value;
  };

  // Get line items directly from record (since we update records immediately via callback)
  const getLineItems = (record) => {
    // Prefer normalized data, fallback to mock data structure
    return record.rec_normalized?.line_items || record.lineItems || record.line_items || [];
  };

  // Handle field editing
  const handleFieldEdit = (recordId, fieldName) => {
    setEditingField({ recordId, fieldName });
  };

  // Handle field change
  const handleFieldChange = (recordId, fieldName, newValue) => {
    // Track changes for save button
    const newEdited = new Map(editedRecords);
    const recordEdits = newEdited.get(recordId) || {};
    recordEdits[fieldName] = newValue;
    newEdited.set(recordId, recordEdits);
    setEditedRecords(newEdited);
    
    // Update local records for immediate visual feedback
    setLocalRecords(prev => prev.map(record => {
      if (record.id === recordId) {
        if (fieldName === 'rec_normalized') {
          return {
            ...record,
            rec_normalized: {
              ...record.rec_normalized,
              ...newValue
            }
          };
        } else {
          return {
            ...record,
            [fieldName]: newValue
          };
        }
      }
      return record;
    }));
  };

  // Save record changes
  const saveRecordChanges = async (recordId) => {
    const changes = editedRecords.get(recordId);
    if (!changes || Object.keys(changes).length === 0) return;

    // Add to saving records set
    setSavingRecords(prev => new Set([...prev, recordId]));

    try {
      await onRecordUpdate?.(recordId, changes);
      
      // Clear the edited record from tracking
      const newEdited = new Map(editedRecords);
      newEdited.delete(recordId);
      setEditedRecords(newEdited);
    } catch (error) {
      console.error('Failed to save record changes:', error);
      // Re-throw error so UI can handle it if needed
      throw error;
    } finally {
      // Remove from saving records set
      setSavingRecords(prev => {
        const newSaving = new Set(prev);
        newSaving.delete(recordId);
        return newSaving;
      });
    }
  };

  // Check if record has unsaved changes
  const hasUnsavedChanges = (recordId) => {
    const changes = editedRecords.get(recordId);
    return changes && Object.keys(changes).length > 0;
  };

  // Handle column header click for sorting
  const handleSort = (columnKey, fieldDef) => {
    // Don't sort on select, actions, or line_items columns
    if (['select', 'actions', 'line_items'].includes(columnKey)) return;

    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === columnKey && sortConfig.direction === 'desc') {
      direction = null; // Remove sort
    }

    setSortConfig({ key: direction ? columnKey : null, direction });
  };

  // Sort records based on current sort configuration
  const sortedRecords = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return localRecords;
    }

    const sorted = [...localRecords].sort((a, b) => {
      const aValue = getFieldValue(a, sortConfig.key);
      const bValue = getFieldValue(b, sortConfig.key);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      // Find the field definition to determine type
      const column = columns.find(col => col.key === sortConfig.key);
      const fieldDef = column?.fieldDef;
      const fieldType = fieldDef?.type;

      // Handle numeric/currency fields
      if (fieldType === 'number' || fieldType === 'currency' || 
          sortConfig.key.includes('amount') || sortConfig.key.includes('balance')) {
        const numA = parseFloat(aValue) || 0;
        const numB = parseFloat(bValue) || 0;
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      }

      // Handle date fields
      if (fieldType === 'date' || sortConfig.key.includes('date')) {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        return sortConfig.direction === 'asc' ? 
          dateA.getTime() - dateB.getTime() : 
          dateB.getTime() - dateA.getTime();
      }

      // Handle record_type column specially
      if (sortConfig.key === 'record_type') {
        const typeA = Array.isArray(a.record_type) ? a.record_type[0] : a.record_type;
        const typeB = Array.isArray(b.record_type) ? b.record_type[0] : b.record_type;
        const compare = (typeA || '').localeCompare(typeB || '');
        return sortConfig.direction === 'asc' ? compare : -compare;
      }

      // Default to string comparison
      const strA = String(aValue).toLowerCase();
      const strB = String(bValue).toLowerCase();
      const compare = strA.localeCompare(strB);
      return sortConfig.direction === 'asc' ? compare : -compare;
    });

    return sorted;
  }, [localRecords, sortConfig, columns]);

  // Line item operations - Updated to use onRecordUpdate immediately like original
  const handleLineItemUpdate = async (transactionId, lineItemId, updates) => {
    const record = localRecords.find(r => r.id === transactionId);
    if (!record) return;

    // Get current line items
    const currentLineItems = record.rec_normalized?.line_items || record.lineItems || [];
    
    // Update the specific line item
    const updatedLineItems = currentLineItems.map((li, index) => {
      if (li.id === lineItemId || index === lineItemId) {
        return { ...li, ...updates };
      }
      return li;
    });

    // Track changes in editedRecords to show save button
    const recordUpdates = {
      rec_normalized: {
        ...record.rec_normalized,
        line_items: updatedLineItems
      }
    };
    
    // Use handleFieldChange to track the change and show save button
    handleFieldChange(transactionId, 'rec_normalized', recordUpdates.rec_normalized);
  };


  const handleDeleteLineItem = async (transactionId, lineItemIndex, amountToAdd) => {
    const record = localRecords.find(r => r.id === transactionId);
    if (!record) return;

    const currentLineItems = [...(record.rec_normalized?.line_items || record.lineItems || [])];
    
    if (currentLineItems.length <= 1) return;

    // Add amount to adjacent line item
    let targetIndex = lineItemIndex > 0 ? lineItemIndex - 1 : lineItemIndex + 1;
    if (currentLineItems[targetIndex]) {
      const currentAmount = parseFloat(currentLineItems[targetIndex].amount) || 0;
      currentLineItems[targetIndex] = {
        ...currentLineItems[targetIndex],
        amount: currentAmount + amountToAdd
      };
    }

    // Remove the deleted line item
    currentLineItems.splice(lineItemIndex, 1);

    // Track changes in editedRecords to show save button
    const recordUpdates = {
      rec_normalized: {
        ...record.rec_normalized,
        line_items: currentLineItems
      }
    };
    
    // Use handleFieldChange to track the change and show save button
    handleFieldChange(transactionId, 'rec_normalized', recordUpdates.rec_normalized);
  };

  const handleLineItemAccountChange = (transactionId, lineItemId, newAccount) => {
    handleLineItemUpdate(transactionId, lineItemId, { account: newAccount });
  };

  // Render line items cell
  const renderLineItemsCell = (record) => {
    const lineItems = getLineItems(record);
    
    if (lineItems.length === 0) {
      return <span className={styles.noLineItems}>No items</span>;
    }

    return (
      <div className={styles.lineItemsCell}>
        <div className={styles.lineItemsText}>
          {lineItems.length === 1 ? (lineItems[0]?.name || 'item') : 'Split'}
        </div>
        <div
          className={styles.lineItemsCount}
          onClick={(e) => {
            e.stopPropagation();
            setOpenLineItemsPanel(openLineItemsPanel === record.id ? null : record.id);
          }}
        >
          {lineItems.length} → line item{lineItems.length > 1 ? 's' : ''}
        </div>
      </div>
    );
  };

  // Check if record type supports splitting
  const canSplitRecordType = (recordType) => {
    // Handle both string and array record_type
    const type = Array.isArray(recordType) ? recordType[0] : recordType;
    return type === 'Purchase' || type === 'Bill' || type === 'Check';
  };

  // Render table cell
  const renderCell = (record, column) => {
    const { key, fieldDef } = column;
    
    if (key === 'select') {
      return (
        <input
          type="checkbox"
          checked={selectedRecords.has(record.id)}
          onChange={() => onRecordSelect?.(record.id)}
          className={styles.checkbox}
        />
      );
    }
    
    if (key === 'error') {
      return (
        <div className={styles.errorCell}>
          <span className={styles.errorText}>{column.label}</span>
        </div>
      );
    }
    
    if (key === 'actions') {
      return (
        <div className={styles.actions}>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(record);
            }}
            className={styles.actionButton}
            title="View details and history"
          >
            <Eye className={styles.actionIcon} />
          </button>
          {hasUnsavedChanges(record.id) ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                saveRecordChanges(record.id);
              }}
              className={styles.saveButton}
              disabled={savingRecords.has(record.id)}
              title={savingRecords.has(record.id) ? "Saving..." : "Save changes"}
            >
              {savingRecords.has(record.id) ? (
                <div className={styles.spinner}></div>
              ) : (
                <Save className={styles.actionIcon} />
              )}
            </button>
          ) : (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onToggleResolved?.(record.id);
              }}
              className={`${styles.resolveButton} ${record.resolved ? styles.resolved : styles.unresolved}`}
              title={record.resolved ? 'Mark unresolved' : 'Mark resolved'}
            >
              <ThumbsUp className={styles.actionIcon} />
              <span className={styles.resolveButtonText}>RESOLVE</span>
            </button>
          )}
        </div>
      );
    }
    
    if (key === 'record_type') {
      const recordType = Array.isArray(record.record_type) ? record.record_type[0] : record.record_type;
      return (
        <div className={styles.recordTypeCell}>
          <span className={styles.recordType}>{recordType}</span>
          {record.deep_link && (
            <a
              href={record.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.deepLink}
              onClick={e => e.stopPropagation()}
              title={`Open ${record.name || 'record'} in ${recordType === 'Purchase' ? 'QuickBooks' : 'accounting system'}`}
            >
              {record.name || 'Edit'}
            </a>
          )}
        </div>
      );
    }
    
    if (key === 'rule_name') {
      const ruleName = getFieldValue(record, 'rule_name');
      return (
        <div className={styles.monitorCell} title={ruleName || ''}>
          <span className={styles.ruleName}>{ruleName || ''}</span>
        </div>
      );
    }
    
    if (key === 'line_items') {
      return renderLineItemsCell(record);
    }
    
    // Handle schema-defined fields
    if (fieldDef) {
      const isEditing = editingField?.recordId === record.id && editingField?.fieldName === key;
      
      return (
        <div
          className={fieldDef.editable ? styles.alwaysEditableField : styles.clickableField}
          onClick={() => fieldDef.editable && handleFieldEdit(record.id, key)}
        >
          <SchemaFieldRenderer
            fieldSchema={fieldDef}
            fieldName={key}
            value={getFieldValue(record, key)}
            onChange={(newValue) => handleFieldChange(record.id, key, newValue)}
            onBlur={() => setEditingField(null)}
            isEditing={isEditing}
            lookupData={lookupData}
            autoFocus={isEditing}
          />
        </div>
      );
    }
    
    // Fallback for unknown fields
    const value = getFieldValue(record, key);
    if (value === null || value === undefined) {
      return <span>N/A</span>;
    }
    
    // Format currency fields
    if (key.includes('amount') || key.includes('balance')) {
      return <span>{Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>;
    }
    
    return <span>{value.toString()}</span>;
  };

  if (loading) {
    return <div className={styles.loading}>Loading schemas...</div>;
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table}>
        {/* Table Header */}
        <div className={styles.tableHeader}>
          <div 
            className={styles.tableHeaderRow}
            style={{ gridTemplateColumns: columns.map(col => col.width || '1fr').join(' ') }}
          >
            {columns.map(column => (
              <div 
                key={column.key} 
                className={`${styles.tableHeaderCell} ${column.sortable ? styles.sortable : ''}`}
                onClick={() => column.sortable && handleSort(column.key, column.fieldDef)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
              >
                <span>{column.label}</span>
                {column.sortable && sortConfig.key === column.key && (
                  <span className={styles.sortIndicator}>
                    {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Table Body */}
        <div className={styles.tableBody}>
          {sortedRecords.map(record => (
            <React.Fragment key={record.id}>
              <div 
                className={
                  record.resolved
                    ? `${styles.tableRow} ${styles.tableRowResolved}`
                    : styles.tableRow
                }
                style={{ gridTemplateColumns: columns.map(col => col.width || '1fr').join(' ') }}
              >
                {columns.map(column => (
                  <div key={column.key} className={styles.tableCell}>
                    {renderCell(record, column)}
                  </div>
                ))}
              </div>
              
              {/* Line Items Panel - Conditionally rendered */}
              {openLineItemsPanel === record.id && (
                <LineItemsPanel
                  record={record}
                  onLineItemUpdate={handleLineItemUpdate}
                  onDeleteLineItem={handleDeleteLineItem}
                  onAccountChange={handleLineItemAccountChange}
                  onRecordUpdate={(recordId, updates) => {
                    // Track changes to show save button
                    if (updates.rec_normalized) {
                      handleFieldChange(recordId, 'rec_normalized', updates.rec_normalized);
                    } else {
                      // For other updates, track them generically
                      Object.entries(updates).forEach(([key, value]) => {
                        handleFieldChange(recordId, key, value);
                      });
                    }
                  }}
                  lookupData={lookupData}
                  canSplit={canSplitRecordType(record.record_type)}
                  getLineItems={getLineItems}
                  schemas={schemas}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

// Line Items Panel Component
function LineItemsPanel({ record, onLineItemUpdate, onDeleteLineItem, onAccountChange, onRecordUpdate, lookupData, canSplit = false, getLineItems, schemas }) {
  const [autoAdjustedLines, setAutoAdjustedLines] = useState(new Set());
  const [editingAmounts, setEditingAmounts] = useState(new Map()); // Track amounts being edited
  
  const lineItems = getLineItems ? getLineItems(record) : (record.rec_normalized?.line_items || record.lineItems || []);
  
  // Get schema for this record type to determine line item fields
  const recordType = Array.isArray(record.record_type) ? record.record_type[0] : record.record_type;
  
  // Use the passed schemas object which should have been loaded by DynamicTable
  const recordSchema = schemas[recordType];
  const lineItemsSchema = recordSchema?.line_items?.fields || {};
  
  // Generate columns based on schema only - no fallback
  const getLineItemColumns = () => {
    const schemaFields = Object.keys(lineItemsSchema);
    
    if (schemaFields.length > 0) {
      return schemaFields
        .filter(field => lineItemsSchema[field].display_order !== undefined)
        .sort((a, b) => (lineItemsSchema[a].display_order || 999) - (lineItemsSchema[b].display_order || 999));
    }
    
    // No fallback - return empty array to show error
    return [];
  };
  
  const columns = getLineItemColumns();
  
  // If no columns available due to missing schema, show error
  if (columns.length === 0) {
    return (
      <div className={styles.lineItemsPanel}>
        <div className={styles.amountMismatchWarning}>
          <span>Error: No schema available for {recordType} line items</span>
        </div>
      </div>
    );
  }
  
  const totalAmount = lineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);
  const originalTotalAmount = record.rec_normalized?.total_amount || totalAmount;
  const amountDifference = Math.abs(totalAmount - originalTotalAmount);

  // Handle splitting a line item
  const handleStartSplit = (lineItem, lineItemId, lineItemIndex) => {
    const originalAmount = parseFloat(lineItem.amount) || 0;
    const halfAmount = originalAmount / 2;
    
    // Create updated line items array with both the split line and new line
    const updatedLineItems = [...lineItems];
    
    // Update the original line item with half the amount
    updatedLineItems[lineItemIndex] = { ...updatedLineItems[lineItemIndex], amount: halfAmount };
    
    // Create a new line item with the other half
    const newLineItem = {
      id: `split_${Date.now()}`,
      name: `${lineItem.name || 'Line'} - Split`,
      description: lineItem.description || '',
      amount: halfAmount,
      account: lineItem.account || '',
      qty: '',
      rate: ''
    };
    
    // Insert the new line item right after the current one
    updatedLineItems.splice(lineItemIndex + 1, 0, newLineItem);
    
    // Update the entire line items array in one operation
    if (onRecordUpdate) {
      const recordUpdates = {
        rec_normalized: {
          ...record.rec_normalized,
          line_items: updatedLineItems
        }
      };
      onRecordUpdate(record.id, recordUpdates);
    }
  };

  // Handle deleting a line item
  const handleDeleteLineItem = (lineItemIndex) => {
    if (lineItems.length <= 1) return;
    
    const lineItemToDelete = lineItems[lineItemIndex];
    const amountToAdd = parseFloat(lineItemToDelete.amount) || 0;
    
    if (onDeleteLineItem) {
      onDeleteLineItem(record.id, lineItemIndex, amountToAdd);
    }
  };

  // Handle amount change with auto-balancing - Fixed to maintain total
  const handleAmountChange = (lineItemIndex, newAmount) => {
    if (lineItems.length <= 1) {
      // Only one line item, just update it using onLineItemUpdate prop
      const lineItem = lineItems[lineItemIndex];
      onLineItemUpdate && onLineItemUpdate(record.id, lineItem.id || lineItemIndex, { amount: newAmount });
      return;
    }

    // Get the original total that we need to maintain - use the original transaction total
    const originalTotal = record.rec_normalized?.total_amount || record.total_amount || 0;
    
    // Create updated line items array with both changes applied
    const updatedLineItems = [...lineItems];
    
    // For 2 line items, the other one should be: originalTotal - newAmount
    if (lineItems.length === 2) {
      const balanceLineIndex = lineItemIndex === 0 ? 1 : 0;
      const balanceNewAmount = originalTotal - newAmount;
      
      // Update both amounts in the array
      updatedLineItems[lineItemIndex] = { ...updatedLineItems[lineItemIndex], amount: newAmount };
      updatedLineItems[balanceLineIndex] = { ...updatedLineItems[balanceLineIndex], amount: balanceNewAmount };
      
      // Highlight the balanced line
      setAutoAdjustedLines(new Set([balanceLineIndex]));
      setTimeout(() => setAutoAdjustedLines(new Set()), 3000);
    } else {
      // For more than 2 line items, balance against adjacent line while maintaining total
      let balanceLineIndex;
      if (lineItemIndex === 0) {
        balanceLineIndex = 1;
      } else {
        balanceLineIndex = lineItemIndex - 1;
      }
      
      const oldAmount = parseFloat(lineItems[lineItemIndex].amount) || 0;
      const difference = newAmount - oldAmount;
      const balanceOldAmount = parseFloat(lineItems[balanceLineIndex].amount) || 0;
      const balanceNewAmount = balanceOldAmount - difference;
      
      // Update both amounts in the array
      updatedLineItems[lineItemIndex] = { ...updatedLineItems[lineItemIndex], amount: newAmount };
      updatedLineItems[balanceLineIndex] = { ...updatedLineItems[balanceLineIndex], amount: balanceNewAmount };
      
      // Highlight the balanced line
      setAutoAdjustedLines(new Set([balanceLineIndex]));
      setTimeout(() => setAutoAdjustedLines(new Set()), 3000);
    }
    
    // Update the entire line items array using onRecordUpdate
    const recordUpdates = {
      rec_normalized: {
        ...record.rec_normalized,
        line_items: updatedLineItems
      }
    };
    
    onRecordUpdate && onRecordUpdate(record.id, recordUpdates);
  };

  const renderColumnHeader = (columnName) => {
    return columnName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderCell = (lineItem, columnName, itemIndex) => {
    const fieldDef = lineItemsSchema[columnName] || {};
    
    switch (columnName) {
      case 'amount':
        // If not editable according to schema, show as read-only
        if (!fieldDef.editable) {
          const value = lineItem.amount != null ? lineItem.amount : 0;
          return <span>{Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span>;
        }
        
        return (
          <input
            type="number"
            step="0.01"
            value={editingAmounts.get(itemIndex) !== undefined 
              ? editingAmounts.get(itemIndex) 
              : (lineItem.amount != null ? lineItem.amount : 0)
            }
            onChange={(e) => {
              const newEditingAmounts = new Map(editingAmounts);
              newEditingAmounts.set(itemIndex, e.target.value);
              setEditingAmounts(newEditingAmounts);
            }}
            onBlur={(e) => {
              const newAmount = parseFloat(e.target.value) || 0;
              const currentAmount = parseFloat(lineItem.amount) || 0;
              
              if (newAmount !== currentAmount) {
                handleAmountChange(itemIndex, newAmount);
              }
              
              const newEditingAmounts = new Map(editingAmounts);
              newEditingAmounts.delete(itemIndex);
              setEditingAmounts(newEditingAmounts);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.target.blur();
              }
            }}
            className={`${styles.splitTableInput} ${autoAdjustedLines.has(itemIndex) ? styles.autoAdjustedAmount : ''}`}
            placeholder="0.00"
          />
        );
      
      case 'quantity':
        // If not editable according to schema, show as read-only text
        if (!fieldDef.editable) {
          const value = lineItem.quantity || lineItem.qty || '';
          return <span>{value || '-'}</span>;
        }
        
        return (
          <input
            type="number"
            step="1"
            value={lineItem.quantity || lineItem.qty || ''}
            onChange={(e) => onLineItemUpdate && onLineItemUpdate(record.id, lineItem.id || itemIndex, { quantity: e.target.value })}
            className={styles.splitTableInput}
            placeholder="Qty"
          />
        );
      
      case 'rate':
        const rateValue = lineItem.rate || lineItem.UnitPrice;
        return (
          <span>{rateValue ? `$${Number(rateValue).toFixed(2)}` : '-'}</span>
        );
      
      case 'description':
      case 'name':
        return (
          <input
            type="text"
            value={lineItem[columnName] || ''}
            onChange={(e) => onLineItemUpdate && onLineItemUpdate(record.id, lineItem.id || itemIndex, { [columnName]: e.target.value })}
            className={styles.splitTableInput}
            placeholder={renderColumnHeader(columnName)}
            disabled={!fieldDef.editable}
          />
        );
      
      default:
        // Handle lookup fields generically based on schema
        if (fieldDef.type === 'lookup') {
          return (
            <SchemaFieldRenderer
              fieldSchema={fieldDef}
              fieldName={columnName}
              value={lineItem[columnName]}
              onChange={(newValue) => {
                // Extract the ID from the lookup value object if it's an object
                const extractedValue = typeof newValue === 'object' ? newValue.id : newValue;
                onLineItemUpdate && onLineItemUpdate(record.id, lineItem.id || itemIndex, { [columnName]: extractedValue });
              }}
              isEditing={fieldDef.editable}
              lookupData={lookupData}
              className={styles.lineItemLookup}
            />
          );
        }
        
        // Handle other field types or display read-only
        const value = lineItem[columnName];
        if (fieldDef.type === 'number' && value) {
          if (columnName.includes('rate') || columnName.includes('price')) {
            return `$${Number(value).toFixed(2)}`;
          }
          return Number(value).toLocaleString();
        }
        return value || '-';
    }
  };

  return (
    <div className={styles.lineItemsPanel}>
      <table className={styles.lineItemsTable}>
        <thead>
          <tr>
            {columns.map(columnName => (
              <th key={columnName}>{renderColumnHeader(columnName)}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => {
            const lineItemId = li.id || idx;
            
            return (
              <tr key={lineItemId}>
                {columns.map(columnName => (
                  <td key={columnName}>
                    {renderCell(li, columnName, idx)}
                  </td>
                ))}
                <td>
                  {canSplit && (
                    <button
                      className={styles.splitButton}
                      onClick={() => handleStartSplit(li, lineItemId, idx)}
                      title="Split this line item"
                    >
                      <Split className={styles.splitIcon} />
                    </button>
                  )}
                  {lineItems.length > 1 && (
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDeleteLineItem(idx)}
                      title="Delete this line item"
                    >
                      <Trash2 className={styles.deleteIcon} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      {amountDifference > 0.01 && (
        <div className={styles.amountMismatchWarning}>
          <AlertTriangle className={styles.warningIcon} />
          <span>Line item amounts must equal the original transaction total of ${originalTotalAmount.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}

export default DynamicTable;